import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Stack,
  Typography,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  useMediaQuery,
  Avatar,
  Card,
  MenuItem,
  Menu,
  IconButton,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  alpha,
} from "@mui/material";
import { Search, MoreVert, FilterListOutlined, Close, DeleteOutline } from "@mui/icons-material";
import dayjs from "dayjs";

import { imageSource, Strings } from "@app/constants";
import { FormProvider, RHFTextField } from "@app/components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useRequestState } from "@app/hooks";
import { useTheme } from "@mui/material/styles";
import { selectCustomerState, selectDeleteSalesPerson } from "@app/modules/salesperson/selector";
import { customer, deleteSalesPerson } from "@app/modules/salesperson/slice";
import { PATH_DASHBOARD } from "@app/routes/paths";

const formatDate = (dateString?: string | null) => {
  if (!dateString) return "N/A";
  const d = dayjs(dateString);
  if (!d.isValid()) return String(dateString);
  return d.format("DD MMM YYYY");
};

const CustomerPage = () => {
  const methods = useForm();
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<string>("");

  const openFilter = Boolean(filterAnchor);
  const [editBrandDialog, setEditBrandDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [customerToDelete, setCustomerToDelete] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const dispatch = useDispatch();

  const { data: customerData } = useRequestState({
    stateSelector: selectCustomerState,
    errorShown: true,
  });

  const { loading: isDeleting } = useRequestState({
    stateSelector: selectDeleteSalesPerson,
    successMessageShown: true,
    errorShown: true,
    onSuccess: () => {
      setOpenDeleteModal(false);
      setCustomerToDelete(null);
      dispatch(customer(selectedRole ? selectedRole : "Customer"));
    },
  });

  const customerDatshow = Array.isArray(customerData) ? customerData : [];

  const handleDelete = (id: any) => {
    if (id) {
      dispatch(deleteSalesPerson({ salesPersonId: id }));
    }
  };

  useEffect(() => {
    dispatch(customer(selectedRole ? selectedRole : "Customer"));
  }, [selectedRole]);

  const filteredData = customerDatshow.filter((item: any) => {
    const query = searchQuery.toLowerCase();
    const fullName =
      [item.firstName, item.lastName].filter(Boolean).join(" ").toLowerCase() ||
      (item.email ? item.email.toLowerCase() : "");

    const matchesSearch =
      fullName.includes(query) ||
      item.phone?.toLowerCase().includes(query) ||
      item.email?.toLowerCase().includes(query) ||
      item.city?.toLowerCase().includes(query);

    const matchesRole = selectedRole
      ? item.role?.toLowerCase() === selectedRole.toLowerCase()
      : true;

    return matchesSearch && matchesRole;
  });

  const handleExport = () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Customer",
      "Contact No.",
      "Email",
      "State",
      "City",
      "Zip Code",
      "Role",
      "Date",
      "Assigned To",
    ];
    const csvRows = [headers.join(",")];

    filteredData.forEach((item: any) => {
      const customerName =
        [item.firstName, item.lastName].filter(Boolean).join(" ") ||
        item.email?.split("@")[0] ||
        "N/A";

      let phone = item.phone || "N/A";
      if (typeof phone === "object") phone = JSON.stringify(phone);
      const formattedPhone = phone !== "N/A" ? `\t${phone}` : phone;

      let email = item.email || "N/A";
      if (typeof email === "object") email = JSON.stringify(email);

      const role = item.role || "N/A";
      const registeredAt = formatDate(item.createdAt);
      const assignedTo =
        item.assignedSalesperson?.name ||
        (Array.isArray(item.assignedAgents) && item.assignedAgents.length > 0
          ? item.assignedAgents.map((a: any) => a.name || a.firstName).join(", ")
          : "N/A");
      const city = item?.city || "N/A";
      const state = item?.state || "N/A";
      const zipCode = item?.zipCode || "N/A";
      const row = [
        customerName,
        formattedPhone,
        email,
        state,
        city,
        zipCode,
        role,
        registeredAt,
        assignedTo,
      ]
        .map((value) => `"${String(value).replace(/"/g, '""')}"`)
        .join(",");
      csvRows.push(row);
    });

    const csvContent =
      "data:text/csv;charset=utf-8," + encodeURIComponent(csvRows.join("\n"));
    const link = document.createElement("a");
    link.setAttribute("href", csvContent);
    link.setAttribute(
      "download",
      `customers_${selectedRole?.toLowerCase() || "all"}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const displayedProducts = filteredData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <FormProvider methods={methods}>
      <Stack direction="row" sx={{ minHeight: "100vh" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Card
            sx={{
              p: 2,
              flex: 1,
              overflowY: "auto",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap="wrap"
              rowGap={2}
              mb={3}
            >
              <RHFTextField
                name="search"
                placeholder="Search by name, email, phone, city..."
                size="small"
                value={searchQuery}
                onChange={(e: any) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  select
                  size="small"
                  label="Role"
                  value={selectedRole || "All"}
                  onChange={(e) => {
                    const role = e.target.value === "All" ? "" : e.target.value;
                    setSelectedRole(role);
                    setPage(1);
                    if (role) {
                      dispatch(customer({ role }));
                    } else {
                      dispatch(customer("Customer"));
                    }
                  }}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {["Buyer", "Seller", "Investor"].map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleExport}
                  sx={{ height: 40 }}
                >
                  Export
                </Button>
              </Stack>
            </Stack>
            <TableContainer component={Paper} sx={{ pb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      "Customer",
                      "Contact No.",
                      "Email",
                      "City",
                      "Role",
                      "Date",
                      "Assigned To",
                      "Action",
                    ].map((heading) => (
                      <TableCell
                        key={heading}
                        align={heading === "Customer" ? "left" : "center"}
                        sx={{
                          fontWeight: 600,
                          fontFamily: fontFamily,
                          color: "#474747",
                        }}
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedProducts.length > 0 ? (
                    displayedProducts.map((item: any, index: any) => {
                      const customerName =
                        [item.firstName, item.lastName].filter(Boolean).join(" ") ||
                        item.email?.split("@")[0] ||
                        "N/A";
                      const avatarInitial =
                        (item.firstName?.[0] || item.email?.[0] || "?").toUpperCase();

                      const assignedTo =
                        item.assignedSalesperson?.name ||
                        (Array.isArray(item.assignedAgents) && item.assignedAgents.length > 0
                          ? item.assignedAgents.map((a: any) => a.name || a.firstName).join(", ")
                          : "N/A");

                      return (
                        <TableRow
                          key={item.id || index}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            navigate(PATH_DASHBOARD.customerDetails, {
                              state: { customer: item, customerId: item.id },
                            });
                          }}
                        >
                          <TableCell
                            sx={{ color: "#334155", fontWeight: 600 }}
                            align="left"
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  fontSize: 13,
                                  fontWeight: 700,
                                  bgcolor: "#1572D3",
                                }}
                                src={item.profileImage || undefined}
                              >
                                {avatarInitial}
                              </Avatar>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#0F172A",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {customerName}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            sx={{ color: "#475569", fontWeight: 500 }}
                            align="center"
                          >
                            {item.phone || "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{ color: "#475569", fontWeight: 500 }}
                            align="center"
                          >
                            {item.email || "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{ color: "#475569", fontWeight: 500 }}
                            align="center"
                          >
                            {item.city || "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{ color: "#475569", fontWeight: 500 }}
                            align="center"
                          >
                            <Chip
                              label={item.role || "Customer"}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                backgroundColor:
                                  item.role === "Buyer"
                                    ? "#DCFCE7"
                                    : item.role === "Seller"
                                    ? "#FEF3C7"
                                    : "#EDE9FE",
                                color:
                                  item.role === "Buyer"
                                    ? "#15803D"
                                    : item.role === "Seller"
                                    ? "#B45309"
                                    : "#6D28D9",
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{ color: "#475569", fontWeight: 500, whiteSpace: "nowrap" }}
                            align="center"
                          >
                            {formatDate(item.updatedAt)}
                          </TableCell>
                          <TableCell
                            sx={{ color: "#475569", fontWeight: 500 }}
                            align="center"
                          >
                            {assignedTo}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setAnchorEl(e.currentTarget);
                                setSelectedRowId(item.id);
                              }}
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: "#64748B" }}>
                        No customers found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Stack
                direction={isSmallScreen ? "column" : "row"}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                mt={2}
                px={1}
              >
                <Typography
                  sx={{ fontSize: 14, fontFamily: fontFamily, fontWeight: 400, color: "#64748B" }}
                >
                  {filteredData.length > 0
                    ? `Showing ${(page - 1) * itemsPerPage + 1} to ${Math.min(
                        page * itemsPerPage,
                        filteredData.length,
                      )} of ${filteredData.length} entries`
                    : "Showing 0 entries"}
                </Typography>
                <Pagination
                  count={Math.max(1, Math.ceil(filteredData.length / itemsPerPage))}
                  page={page}
                  onChange={(event: any, value: any) => setPage(value)}
                  shape="rounded"
                  color="primary"
                />
              </Stack>
            </TableContainer>
          </Card>
        </Box>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedRowId(null);
        }}
      >
        <MenuItem
          onClick={() => {
            const rowData = filteredData.find((item: any) => item.id === selectedRowId);
            setAnchorEl(null);
            setSelectedRowId(null);
            navigate(PATH_DASHBOARD.customerDetails, {
              state: { customer: rowData, customerId: selectedRowId },
            });
          }}
        >
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            const rowData = filteredData.find((item: any) => item.id === selectedRowId);
            setCustomerToDelete(rowData || { id: selectedRowId });
            setOpenDeleteModal(true);
            setAnchorEl(null);
            setSelectedRowId(null);
          }}
          sx={{ color: "error.main" }}
        >
          {Strings.button.delete}
        </MenuItem>
      </Menu>

      {/* ------------------------------------------------------------- */}
      {/* DELETE CUSTOMER CONFIRMATION POPUP */}
      {/* ------------------------------------------------------------- */}
      <Dialog
        open={openDeleteModal}
        onClose={() => !isDeleting && setOpenDeleteModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "#101828",
              }}
            >
              Delete Customer
            </Typography>
            <IconButton
              disabled={isDeleting}
              onClick={() => {
                setOpenDeleteModal(false);
                setCustomerToDelete(null);
              }}
              size="small"
              sx={{ color: theme.palette.grey[500] }}
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText
            sx={{
              mb: 2,
              fontFamily,
              color: "#474747",
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to delete this customer? This action cannot be undone.
          </DialogContentText>

          {customerToDelete && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: theme.palette.grey[50],
                border: `1px solid #E0E0E0`,
              }}
            >
              <Stack spacing={1.2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                    Name:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                    {[customerToDelete?.firstName, customerToDelete?.lastName].filter(Boolean).join(" ") ||
                      customerToDelete?.email?.split("@")[0] ||
                      `#${customerToDelete?.id}`}
                  </Typography>
                </Stack>
                {customerToDelete?.email && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                      Email:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                      {customerToDelete.email}
                    </Typography>
                  </Stack>
                )}
                {customerToDelete?.phone && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                      Contact No.:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                      {customerToDelete.phone}
                    </Typography>
                  </Stack>
                )}
                {customerToDelete?.role && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                      Role:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: theme.palette.primary.main, fontWeight: 700 }}>
                      {customerToDelete.role}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenDeleteModal(false);
              setCustomerToDelete(null);
            }}
            disabled={isDeleting}
            sx={{
              flex: 1,
              borderRadius: 1,
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              "&:hover": {
                borderColor: theme.palette.primary.dark,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            {Strings.button.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (customerToDelete?.id) {
                dispatch(deleteSalesPerson({ salesPersonId: customerToDelete.id }));
              }
            }}
            disabled={isDeleting}
            startIcon={
              isDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              flex: 1,
              borderRadius: 1,
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              backgroundColor: theme.palette.primary.main,
              color: "white",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {isDeleting ? "Deleting..." : Strings.button.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export { CustomerPage };
